import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Heart, MessageCircle, Share2, Eye, Clock,
  MoreHorizontal, Send, Edit, Trash2, Flag
} from 'lucide-react';
import CommentThread from './CommentThread';
import EditPostInline from './EditPostInline';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const PostDetail = () => {
  const { postId } = useParams();
  const navigate = useNavigate();
  const profile = useSelector(state => state.profile);
  
  const [post, setPost] = useState(null);
  const [categories, setCategories] = useState([]);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    fetchPostDetail();
    fetchCategories();
  }, [postId]);

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}/api/forumV3/categories`);
      if (data.success) {
        setCategories(data.categories);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPostDetail = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/forumV3/posts/${postId}`
      );
      
      if (data.success) {
        setPost(data.post);
        setComments(data.comments || []);
        setIsLiked(data.post.isLikedByUser);
        setLikeCount(data.post.likeCount || 0);
      } else {
        toast.error('Post not found');
        navigate('/home/forum');
      }
    } catch (error) {
      console.error('Error fetching post:', error);
      toast.error('Error loading post');
      navigate('/home/forum');
    } finally {
      setLoading(false);
    }
  };

  const handleLikePost = async () => {
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/forumV3/posts/${postId}/like`,
        { alumniId: profile._id }
      );
      
      if (data.success) {
        setIsLiked(data.isLiked);
        setLikeCount(data.likeCount);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Failed to like post');
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    setSubmittingComment(true);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/forumV3/posts/${postId}/comments`,
        { 
          content: newComment.trim(), 
          alumniId: profile._id
        }
      );
      
      if (data.success) {
        setComments([data.comment, ...comments]);
        setNewComment('');
        setPost(prev => ({
          ...prev,
          commentCount: (prev.commentCount || 0) + 1
        }));
         toast.dismiss() ;  toast.success('Comment added successfully');
      }
    } catch (error) {
      console.error('Error submitting comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeletePost = async () => {
    if (!window.confirm('Are you sure you want to delete this post? This action cannot be undone.')) {
      return;
    }

    try {
      const { data } = await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/forumV3/posts/${postId}`
      );
      
      if (data.success) {
         toast.dismiss() ;  toast.success('Post deleted successfully');
        navigate('/home/forum');
      }
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };

  const handlePostUpdated = (updatedPost) => {
    setPost(updatedPost);
    setIsEditing(false);
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const postDate = new Date(date);
    const diffInHours = Math.floor((now - postDate) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 168) return `${Math.floor(diffInHours / 24)}d ago`;
    return postDate.toLocaleDateString();
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
       toast.dismiss() ;  toast.success('Link copied to clipboard!');
    } catch (error) {
      console.error('Error sharing:', error);
      toast.error('Failed to copy link');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="animate-pulse">
            <div className="bg-white rounded-lg p-4 mb-4 shadow-sm">
              <div className="h-4 bg-gray-300 rounded w-1/4 mb-4"></div>
              <div className="h-6 bg-gray-300 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-300 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-300 rounded w-1/2"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="text-center bg-white rounded-lg p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-2">Post not found</h2>
            <p className="text-gray-600 mb-4">The post you're looking for doesn't exist.</p>
            <button
              onClick={() => navigate('/home/forum')}
              className="dynamic-site-bg text-white px-4 py-2 rounded-lg"
            >
              Back to Forum
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isAuthor = post.alumniId._id === profile._id;
  const isAdmin = profile.admin;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Back Button */}
        <button
          onClick={() => navigate('/home/forum')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4 p-2 hover:bg-white rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
          <span className="hidden sm:inline">Back to Forum</span>
        </button>

        {/* Edit Post Inline */}
        <AnimatePresence>
          {isEditing && (
            <EditPostInline
              post={post}
              categories={categories}
              onCancel={() => setIsEditing(false)}
              onPostUpdated={handlePostUpdated}
            />
          )}
        </AnimatePresence>

        {/* Post Content */}
        <article className="bg-white rounded-lg shadow-sm border border-gray-200 mb-4">
          <div className="p-4">
            {/* Header */}
            <div className="flex items-start gap-3 mb-4">
              <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                {post.alumniId.profilePicture ? (
                  <img
                    src={post.alumniId.profilePicture}
                    alt={`${post.alumniId.firstName} ${post.alumniId.lastName}`}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full dynamic-site-bg flex items-center justify-center text-white font-medium">
                    {post.alumniId.firstName?.[0]}{post.alumniId.lastName?.[0]}
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900">
                    {post.alumniId.firstName} {post.alumniId.lastName}
                  </h3>
                  {post.alumniId.graduation_year && (
                    <span className="text-sm bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">
                      Class of {post.alumniId.graduation_year}
                    </span>
                  )}
                </div>
                
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock size={14} />
                  <span>{formatTimeAgo(post.createdAt)}</span>
                  <span>•</span>
                  <span className="dynamic-site-bg text-white px-2 py-0.5 rounded-full text-xs">
                    {post.categoryId.name}
                  </span>
                  <span>•</span>
                  <span className="flex items-center gap-1">
                    <Eye size={14} />
                    {post.views || 0}
                  </span>
                </div>
              </div>

              <div className="relative">
                <button 
                  onClick={() => setShowDropdown(!showDropdown)}
                  className="w-8 h-8 rounded-lg hover:bg-gray-100 flex items-center justify-center"
                >
                  <MoreHorizontal size={16} className="text-gray-500" />
                </button>

                {showDropdown && (
                  <>
                    <div className="absolute right-0 top-8 w-40 bg-white rounded-lg shadow-lg border py-1 z-20">
                      {isAuthor && (
                        <>
                          <button 
                            onClick={() => {
                              setShowDropdown(false);
                              setIsEditing(true);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <Edit size={14} />
                            Edit Post
                          </button>
                          <button 
                            onClick={() => {
                              setShowDropdown(false);
                              handleDeletePost();
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            <Trash2 size={14} />
                            Delete
                          </button>
                        </>
                      )}
                      
                      {isAdmin && !isAuthor && (
                        <button 
                          onClick={() => {
                            setShowDropdown(false);
                            handleDeletePost();
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                        >
                          <Trash2 size={14} />
                          Delete
                        </button>
                      )}

                      {!isAuthor && (
                        <button className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                          <Flag size={14} />
                          Report
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Title */}
            <h1 className="text-xl lg:text-2xl font-bold text-gray-900 mb-3 leading-tight">
              {post.title}
            </h1>

            {/* Content */}
            <div className="text-gray-700 mb-4 leading-relaxed">
              {post.content.split('\n').map((paragraph, index) => (
                <p key={index} className="mb-3 last:mb-0">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-4">
                {post.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium hover:bg-blue-100 cursor-pointer"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div className="flex items-center gap-4">
                <button
                  onClick={handleLikePost}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors ${
                    isLiked
                      ? 'bg-red-50 text-red-600'
                      : 'bg-gray-50 text-gray-600 hover:bg-red-50 hover:text-red-600'
                  }`}
                >
                  <Heart size={16} className={isLiked ? 'fill-current' : ''} />
                  <span>{likeCount}</span>
                </button>

                <div className="flex items-center gap-2 text-gray-600">
                  <MessageCircle size={16} />
                  <span className="font-medium">{post.commentCount || 0} Comments</span>
                </div>
              </div>

              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-3 py-2 bg-gray-50 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <Share2 size={16} />
                <span className="hidden sm:inline font-medium">Share</span>
              </button>
            </div>
          </div>
        </article>

        {/* Comment Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-4">
          <h3 className="font-semibold text-gray-900 mb-3">Add Comment</h3>
          <form onSubmit={handleSubmitComment}>
            <div className="flex gap-3">
              <div className="w-8 h-8 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                {profile.profilePicture ? (
                  <img
                    src={profile.profilePicture}
                    alt="Your profile"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full dynamic-site-bg flex items-center justify-center text-white font-medium text-xs">
                    {profile.firstName?.[0]}{profile.lastName?.[0]}
                  </div>
                )}
              </div>
              <div className="flex-1">
                <textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Share your thoughts..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                />
                <div className="flex justify-end mt-2">
                  <button
                    type="submit"
                    disabled={!newComment.trim() || submittingComment}
                    className="flex items-center gap-2 px-4 py-2 dynamic-site-bg text-white rounded-lg font-medium hover:opacity-90 disabled:opacity-50"
                  >
                    {submittingComment ? (
                      <div className="w-4 h-4 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                    ) : (
                      <Send size={16} />
                    )}
                    {submittingComment ? 'Posting...' : 'Comment'}
                  </button>
                </div>
              </div>
            </div>
          </form>
        </div>

        {/* Comments Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-4 border-b border-gray-200">
            <h3 className="font-semibold text-gray-900">
              Comments ({comments.length})
            </h3>
          </div>
          
          <div className="divide-y divide-gray-200">
            {comments.length > 0 ? (
              comments.map((comment) => (
                <CommentThread
                  key={comment._id}
                  comment={comment}
                  postId={postId}
                  onCommentUpdate={() => fetchPostDetail()}
                  level={0}
                />
              ))
            ) : (
              <div className="p-8 text-center">
                <MessageCircle size={32} className="mx-auto text-gray-400 mb-3" />
                <h4 className="font-medium text-gray-900 mb-1">No comments yet</h4>
                <p className="text-gray-600 text-sm">Be the first to share your thoughts!</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PostDetail;
